import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { dayText, galText } from "./accounts";
import * as api from "./api";
import { allPicked, toggled, withAll } from "./bulk";
import { PLACE_ENTRY_COLUMNS, REVIEW_COLUMNS } from "./columns";
import { go, useTitle } from "./hooks";
import {
  ackChunks,
  ackedText,
  ackText,
  assigneeText,
  averagesText,
  awayText,
  containersCell,
  containersText,
  correctHref,
  countText,
  decisionBody,
  decisionError,
  type DecisionField,
  type DecisionForm,
  defaultDrop,
  DUPLICATE_FIRST,
  duplicateKey,
  duplicatePickupText,
  KIND_TEXT,
  noPresetText,
  oldEntriesText,
  oldEntryChunks,
  oldEntryWhat,
  oldEntryWhen,
  RESULT_TEXT,
  RESULTS,
  type ReviewResult,
  reviewRecommendedCell,
  SAVE_TEXT,
  salesFirst,
  shownPickups,
  weeksText,
  weekShort,
  weekText,
  withoutOldEntries,
  withoutWeeks,
} from "./review";
import { personName } from "./requests";
import { placeMark } from "./place";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { pullSizes } from "./workOrders";
import { Hint } from "./Hint";
import { REVIEW } from "./store/pageViews";
import { loadFailure, useView, ViewFreshness } from "./store/useView";
import { Skeleton } from "./Skeleton";

const EMPTY: DecisionForm = { note: "", rate: "", weeks: "", pull: null, drop: null, assignee: "" };

function FieldError({ id, text }: { id: string; text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="error" id={id} data-testid={id}>
      {text}
    </p>
  );
}

function Decide({
  item,
  result,
  sizes,
  auth,
  user,
  onSaved,
  onClose,
}: {
  item: api.ReviewItem;
  result: ReviewResult;
  sizes: number[];
  auth: Auth;
  user: User;
  onSaved: (next: api.Review, text: string) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<DecisionForm>({
    ...EMPTY,
    rate: item.measured_rate.toFixed(1),
    drop: defaultDrop(item),
  });
  const [people, setPeople] = useState<api.Assignee[] | null>(null);
  const [problem, setProblem] = useState<{ field: DecisionField | null; text: string } | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const decisionId = useRef<string | null>(null);
  const first = useRef<HTMLElement | null>(null);
  const prefix = `decide-${item.kind}-${item.account_id}`;
  useEffect(() => first.current?.focus(), []);
  useEffect(() => {
    if (result !== "sales") return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.assignees(token, item.account_id))
      .then((found) => live && setPeople(salesFirst(found)))
      .catch(() => live && setPeople([]));
    return () => {
      live = false;
    };
  }, [result, user, item.account_id]);
  const edit = (change: Partial<DecisionForm>) => {
    setForm((old) => ({ ...old, ...change }));
    decisionId.current = null;
    setProblem(null);
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const found = decisionError(result, form, item);
    if (found) {
      setProblem(found);
      return;
    }
    decisionId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const body = decisionBody(decisionId.current, item.kind, result, form);
      const next = await api.decideReview(await user.getIdToken(), item.account_id, body);
      decisionId.current = null;
      onSaved(next, `Saved: ${RESULT_TEXT[result]} on ${item.number} ${item.name}.`);
    } catch (e) {
      const failed = saveFailure(e, "The decision");
      if (!failed.ok && failed.field) setProblem({ field: null, text: failed.text });
      else setSaved(failed);
    } finally {
      setSaving(false);
    }
  };
  const errorOf = (field: DecisionField) => (problem?.field === field ? problem.text : null);
  const invalid = (field: DecisionField) => (problem?.field === field ? true : undefined);
  const ref = (node: HTMLElement | null) => {
    first.current ??= node;
  };
  return (
    <form onSubmit={submit} noValidate className="step-form" data-testid="decision-form" aria-label={RESULT_TEXT[result]}>
      {result === "rate" && (
        <>
          <label htmlFor={`${prefix}-rate`}>New stated rate (gal a week)</label>
          <input
            ref={ref}
            id={`${prefix}-rate`}
            data-testid="decision-rate"
            inputMode="decimal"
            value={form.rate}
            aria-invalid={invalid("rate")}
            aria-describedby={errorOf("rate") ? `${prefix}-rate-error` : undefined}
            onChange={(e) => edit({ rate: e.target.value })}
          />
          <FieldError id={`${prefix}-rate-error`} text={errorOf("rate")} />
        </>
      )}
      {result === "week_group" && (
        <>
          <label htmlFor={`${prefix}-weeks`}>New week group, for example 1-3</label>
          <input
            ref={ref}
            id={`${prefix}-weeks`}
            data-testid="decision-weeks"
            value={form.weeks}
            aria-invalid={invalid("weeks")}
            aria-describedby={errorOf("weeks") ? `${prefix}-weeks-error` : undefined}
            onChange={(e) => edit({ weeks: e.target.value })}
          />
          <FieldError id={`${prefix}-weeks-error`} text={errorOf("weeks")} />
        </>
      )}
      {result === "resize" && (
        <>
          <label htmlFor={`${prefix}-pull`}>Container to pull</label>
          <select
            ref={ref}
            id={`${prefix}-pull`}
            data-testid="decision-pull"
            value={form.pull ?? ""}
            onChange={(e) => edit({ pull: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">
              {item.sizes.length ? `${galText(Math.max(...item.sizes))} (on the account)` : "The largest container on the account"}
            </option>
            {pullSizes(item.sizes, sizes).map((size) => (
              <option key={size} value={size}>
                {galText(size)}
              </option>
            ))}
          </select>
          <label htmlFor={`${prefix}-drop`}>Size to drop</label>
          <select
            id={`${prefix}-drop`}
            data-testid="decision-drop"
            value={form.drop ?? ""}
            aria-invalid={invalid("drop")}
            aria-describedby={errorOf("drop") ? `${prefix}-drop-error` : undefined}
            onChange={(e) => edit({ drop: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Choose a size</option>
            {sizes.map((size) => (
              <option key={size} value={size}>
                {galText(size)}
              </option>
            ))}
          </select>
          <FieldError id={`${prefix}-drop-error`} text={errorOf("drop")} />
          {noPresetText(item, form.pull) && (
            <p className="sub" data-testid="decision-no-preset">
              {noPresetText(item, form.pull)}
            </p>
          )}
        </>
      )}
      {result === "sales" && (
        <>
          <label htmlFor={`${prefix}-assignee`}>Who takes the sales issue</label>
          <select
            ref={ref}
            id={`${prefix}-assignee`}
            data-testid="decision-assignee"
            value={form.assignee}
            disabled={people === null}
            aria-invalid={invalid("assignee")}
            aria-describedby={errorOf("assignee") ? `${prefix}-assignee-error` : undefined}
            onChange={(e) => edit({ assignee: e.target.value })}
          >
            <option value="">{people === null ? "Loading…" : "Choose a member"}</option>
            {(people ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {assigneeText(person)}
              </option>
            ))}
          </select>
          <FieldError id={`${prefix}-assignee-error`} text={errorOf("assignee")} />
        </>
      )}
      <label htmlFor={`${prefix}-note`}>{result === "no_change" ? "Reason for no change" : "Note (optional)"}</label>
      <textarea
        ref={ref}
        id={`${prefix}-note`}
        data-testid="decision-note"
        rows={2}
        value={form.note}
        aria-invalid={invalid("note")}
        aria-describedby={errorOf("note") ? `${prefix}-note-error` : undefined}
        onChange={(e) => edit({ note: e.target.value })}
      />
      <FieldError id={`${prefix}-note-error`} text={errorOf("note")} />
      <p className="sub" data-testid="decision-fact">
        A decision is a record. It has no undo.
      </p>
      {problem && problem.field === null && <FieldError id={`${prefix}-error`} text={problem.text} />}
      <div className="actions">
        <button className="primary" data-testid="save-decision" disabled={saving}>
          {saving ? "Saving…" : SAVE_TEXT[result]}
        </button>
        <button type="button" data-testid="close-decision" onClick={onClose}>
          Close
        </button>
      </div>
      {saved && !saved.ok && (
        <p role="alert" className="error">
          {saved.text}
          {saved.session && (
            <>
              {" "}
              <button type="button" onClick={() => signOut(auth)}>
                Sign in again
              </button>
            </>
          )}
        </p>
      )}
    </form>
  );
}

const SPAN = REVIEW_COLUMNS.length + 1;

function ReviewRow({
  item,
  base,
  sizes,
  auth,
  user,
  panel,
  onPanel,
  onSaved,
}: {
  item: api.ReviewItem;
  base: string;
  sizes: number[];
  auth: Auth;
  user: User;
  panel: ReviewResult | "open" | null;
  onPanel: (next: ReviewResult | "open" | null) => void;
  onSaved: (next: api.Review, text: string) => void;
}) {
  const prefix = `review-${item.kind}-${item.account_id}`;
  const recommended = reviewRecommendedCell(item);
  const containers = containersCell(item);
  const [focus, setFocus] = useState<string | null>(null);
  useEffect(() => {
    if (focus === null) return;
    document.getElementById(focus)?.focus();
    setFocus(null);
  }, [focus]);
  const move = (next: ReviewResult | "open" | null, target: string | null) => {
    onPanel(next);
    setFocus(target);
  };
  return (
    <tbody className="item" data-testid="review-item" data-kind={item.kind} data-number={item.number}>
      <tr>
        <th scope="row" data-label="" className="wrap">
          <a href={`${base}/accounts/${item.account_id}`} onClick={go} data-testid="review-account" className="name" title={`${item.number} ${item.name}`}>
            {item.number} {item.name}
          </a>
        </th>
        <td data-label="Reason" title={item.reason} data-testid="review-short">
          {item.short}
        </td>
        <td
          data-label="Rate (stated → measured)"
          className="num"
          title={`Stated ${item.stated_rate.toFixed(1)} gal a week, measured ${item.measured_rate.toFixed(1)} gal a week`}
        >
          {item.stated_rate.toFixed(1)} → {item.measured_rate.toFixed(1)}
        </td>
        <td data-label="Containers" className="phone-hide" title={containers.title}>
          {containers.text}
        </td>
        <td data-label="Recommended" data-testid="review-recommended" title={recommended.title}>
          {recommended.text}
        </td>
        <td data-label="" className="row-actions sticky-end">
          {item.can_decide && (
            <button
              type="button"
              id={`${prefix}-decide`}
              data-testid="review-decide"
              aria-expanded={panel !== null}
              aria-controls={`${prefix}-panel`}
              onClick={() => move(panel === null ? "open" : null, null)}
            >
              Decide…
            </button>
          )}
        </td>
      </tr>
      {panel !== null && (
        <tr className="detail-row" id={`${prefix}-panel`}>
          <td colSpan={SPAN}>
            <p data-testid="review-reason">{item.reason}</p>
            <p className="sub">
              Averages, newest first: {averagesText(item.averages)}. Containers: {containersText(item)}.
            </p>
            {panel === "open" ? (
              <p className="actions review-results">
                {RESULTS.map((result) => (
                  <button
                    key={result}
                    type="button"
                    id={`${prefix}-result-${result}`}
                    data-testid={`review-result-${result}`}
                    onClick={() => move(result, null)}
                  >
                    {RESULT_TEXT[result]}…
                  </button>
                ))}
                <button type="button" data-testid="close-panel" onClick={() => move(null, `${prefix}-decide`)}>
                  Close
                </button>
              </p>
            ) : (
              <Decide
                item={item}
                result={panel}
                sizes={sizes}
                auth={auth}
                user={user}
                onSaved={onSaved}
                onClose={() => move("open", `${prefix}-result-${panel}`)}
              />
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
}

/** Design 8.8 step 3: two or more pickups on one account in one week. A person decides; nothing merges. */
function Duplicates({
  items,
  base,
  auth,
  user,
  onSaved,
}: {
  items: api.DuplicateItem[];
  base: string;
  auth: Auth;
  user: User;
  onSaved: (next: (before: api.Review) => api.Review, text: string) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<Saved | null>(null);
  const ackable = items.filter((item) => item.can_ack);
  const keys = ackable.map(duplicateKey);
  const chosen = ackable.filter((item) => picked.has(duplicateKey(item)));
  const ack = async (weeks: api.DuplicateItem[]) => {
    setBusy(true);
    setFailed(null);
    try {
      let acked = 0;
      let unchanged = 0;
      for (const chunk of ackChunks(weeks)) {
        const reply = await api.ackDuplicates(await user.getIdToken(), chunk);
        acked += reply.acked;
        unchanged += reply.unchanged;
      }
      // A week that changed after the load stays open with its new pickups, so load the review again.
      const fresh = unchanged > 0 ? await api.review(await user.getIdToken()) : null;
      setPicked(new Set());
      const what = weeks.length === 1 ? `${weeks[0].number} ${weeks[0].name}, week of ${weekText(weeks[0])}` : weeksText(acked);
      const answered = new Set(weeks.map(duplicateKey));
      onSaved((before) => fresh ?? withoutWeeks(before, answered), `Saved: ${ackedText(weeks)} are right on ${what}.`);
    } catch (e) {
      setFailed(saveFailure(e, "The answer"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="review-duplicates" data-testid="review-duplicates">
      <h2 id="review-duplicates">
        Possible duplicate pickups <span className="sub">({weeksText(items.length)})</span>
      </h2>
      <p className="sub">
        An account has two or more pickups in one week. Keep both, or correct one. Nothing merges by itself. An answer is a record. It
        has no undo.
      </p>
      {ackable.length > 0 && (
        <p className="bulk-bar" data-testid="duplicate-bulk-bar">
          <label className="check">
            <input
              type="checkbox"
              data-testid="duplicate-pick-all"
              checked={allPicked(picked, keys)}
              onChange={(e) => setPicked((before) => withAll(before, keys, e.target.checked))}
            />{" "}
            Select all ({ackable.length.toLocaleString("en-US")})
          </label>{" "}
          <span data-testid="duplicate-count">{chosen.length.toLocaleString("en-US")} selected</span>{" "}
          <button type="button" data-testid="duplicate-ack-picked" disabled={busy || chosen.length === 0} onClick={() => ack(chosen)}>
            {busy ? "Saving…" : `${ackText(chosen)} on the selected weeks`}
          </button>
        </p>
      )}
      {failed && !failed.ok && (
        <p role="alert" className="error" data-testid="duplicate-error">
          {failed.text}
          {failed.session && (
            <>
              {" "}
              <button type="button" onClick={() => signOut(auth)}>
                Sign in again
              </button>
            </>
          )}
        </p>
      )}
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Possible duplicate pickups">
        <table className="list review-table duplicate-table" data-testid="duplicate-table">
          <thead>
            <tr>
              {ackable.length > 0 && (
                <th scope="col">
                  <span className="visually-hidden">Select</span>
                </th>
              )}
              <th scope="col">Account</th>
              <th scope="col">Week</th>
              <th scope="col" className="wrap">
                Pickups (raw/scrap)
              </th>
              <th scope="col" className="sticky-end">
                <span className="visually-hidden">Answer</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const key = duplicateKey(item);
              return (
                <tr key={key} data-testid="duplicate-item" data-number={item.number} data-week={item.week}>
                  {ackable.length > 0 && (
                    <td data-label="">
                      {item.can_ack && (
                        <input
                          type="checkbox"
                          data-testid="duplicate-pick"
                          aria-label={`Select ${item.number}, week of ${weekText(item)}`}
                          checked={picked.has(key)}
                          onChange={() => setPicked((before) => toggled(before, key))}
                        />
                      )}
                    </td>
                  )}
                  <th scope="row" data-label="" className="wrap">
                    <a href={`${base}/accounts/${item.account_id}`} onClick={go} className="name" title={`${item.number} ${item.name}`}>
                      {item.number} {item.name}
                    </a>
                  </th>
                  <td data-label="Week">{weekShort(item)}</td>
                  <td data-label="Pickups" className="wrap">
                    <ul className="duplicate-pickups" data-testid="duplicate-pickups">
                      {shownPickups(item, opened.has(key)).map((pickup) => (
                        <li key={pickup.id}>
                          {item.can_ack && (
                            <>
                              <a
                                href={correctHref(base, item, pickup)}
                                onClick={go}
                                data-testid="duplicate-correct"
                                aria-label={`Correct the pickup of ${duplicatePickupText(pickup)}`}
                              >
                                Correct
                              </a>{" "}
                              ·{" "}
                            </>
                          )}
                          {duplicatePickupText(pickup)}
                        </li>
                      ))}
                    </ul>
                    {!opened.has(key) && item.pickups.length > DUPLICATE_FIRST && (
                      <button
                        type="button"
                        data-testid="duplicate-show-all"
                        onClick={() => setOpened((before) => toggled(before, key))}
                      >
                        Show all {item.pickups.length.toLocaleString("en-US")} pickups
                      </button>
                    )}
                  </td>
                  <td data-label="" className="row-actions sticky-end duplicate-answer">
                    {item.can_ack && (
                      <button type="button" data-testid="duplicate-ack" disabled={busy} onClick={() => ack([item])}>
                        {ackText([item])}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** SO-956: a phone visit older than the offline window. The office checks it, or corrects it. */
/** Rule D-19: the phone entries that the driver confirmed away from the stop. Only to see; no answer. */
function PlaceEntries({ items, base, timeZone }: { items: api.PlaceEntry[]; base: string; timeZone: string }) {
  const [sort, setSort] = useSort("asort", PLACE_ENTRY_COLUMNS);
  const rows = sortRows(items, PLACE_ENTRY_COLUMNS, sort);
  return (
    <section aria-labelledby="review-place-entries" data-testid="review-place-entries" id="away">
      <h2 id="review-place-entries" tabIndex={-1}>
        Saved away from the stop <span className="sub">({oldEntriesText(items.length)}, last 7 days)</span>
      </h2>
      <p className="sub">The driver saved these on a phone that was far from the stop pin, or that had no position, and confirmed that they were at the stop.</p>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Entries saved away from the stop">
        <table className="list review-table place-entry-table" data-testid="place-entry-table">
          <thead>
            <tr>
              {PLACE_ENTRY_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const when = oldEntryWhen(item.at, timeZone);
              const mark = placeMark(item.place) ?? "";
              return (
                <tr key={item.visit_id} data-testid="place-entry-item" data-number={item.number} data-check={item.place.check}>
                  <th scope="row" data-label="" className="wrap">
                    <a href={`${base}/accounts/${item.account_id}`} onClick={go} className="name" title={`${item.number} ${item.name}`}>
                      {item.number} {item.name}
                    </a>
                  </th>
                  <td data-label="Visit">
                    <a href={`${base}/visits?day=${when.day}`} onClick={go}>
                      {when.text}
                    </a>
                  </td>
                  <td data-label="Entry">
                    <span className="clip" title={oldEntryWhat(item)}>
                      {oldEntryWhat(item)}
                    </span>
                  </td>
                  <td data-label="Driver">
                    {item.driver_name ? (
                      <span className="clip short" title={item.driver_name}>
                        {personName(item.driver_name)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td data-label="Place" data-testid="place-entry-mark">
                    <span className="place-mark" title={mark}>
                      <span aria-hidden="true">⚠ </span>
                      {mark}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OldEntries({
  items,
  base,
  timeZone,
  auth,
  user,
  onSaved,
}: {
  items: api.OldEntry[];
  base: string;
  timeZone: string;
  auth: Auth;
  user: User;
  onSaved: (next: (before: api.Review) => api.Review, text: string) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<Saved | null>(null);
  const checkable = items.filter((item) => item.can_check);
  const keys = checkable.map((item) => item.visit_id);
  const chosen = keys.filter((id) => picked.has(id));
  const check = async (ids: string[]) => {
    setBusy(true);
    setFailed(null);
    try {
      let checked = 0;
      for (const chunk of oldEntryChunks(ids)) checked += (await api.checkOldEntries(await user.getIdToken(), chunk)).checked;
      setPicked(new Set());
      const one = ids.length === 1 ? items.find((item) => item.visit_id === ids[0]) : undefined;
      const what = one ? `${one.number} ${one.name}, ${oldEntryWhen(one.at, timeZone).text}` : oldEntriesText(checked);
      onSaved((before) => withoutOldEntries(before, new Set(ids)), `Checked: ${what}.`);
    } catch (e) {
      setFailed(saveFailure(e, "The check"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="review-old-entries" data-testid="review-old-entries">
      <h2 id="review-old-entries">
        Old entry to check <span className="sub">({oldEntriesText(items.length)})</span>
      </h2>
      <p className="sub">
        A phone sent a visit older than the offline window. The driver confirmed the date. Check that it is correct, or correct the visit.
      </p>
      {checkable.length > 0 && (
        <p className="bulk-bar" data-testid="old-entry-bulk-bar">
          <label className="check">
            <input
              type="checkbox"
              data-testid="old-entry-pick-all"
              checked={allPicked(picked, keys)}
              onChange={(e) => setPicked((before) => withAll(before, keys, e.target.checked))}
            />{" "}
            Select all ({checkable.length.toLocaleString("en-US")})
          </label>{" "}
          <span data-testid="old-entry-count">{chosen.length.toLocaleString("en-US")} selected</span>{" "}
          <button type="button" data-testid="old-entry-check-picked" disabled={busy || chosen.length === 0} onClick={() => check(chosen)}>
            {busy ? "Saving…" : "Checked"}
          </button>
        </p>
      )}
      {failed && !failed.ok && (
        <p role="alert" className="error" data-testid="old-entry-error">
          {failed.text}
          {failed.session && (
            <>
              {" "}
              <button type="button" onClick={() => signOut(auth)}>
                Sign in again
              </button>
            </>
          )}
        </p>
      )}
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Old entries to check">
        <table className="list review-table old-entry-table" data-testid="old-entry-table">
          <thead>
            <tr>
              {checkable.length > 0 && (
                <th scope="col">
                  <span className="visually-hidden">Select</span>
                </th>
              )}
              <th scope="col">Account</th>
              <th scope="col">Visit</th>
              <th scope="col">Entry</th>
              <th scope="col">Driver</th>
              <th scope="col">Sent</th>
              <th scope="col" className="sticky-end">
                <span className="visually-hidden">Answer</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const when = oldEntryWhen(item.at, timeZone);
              return (
                <tr key={item.visit_id} data-testid="old-entry-item" data-number={item.number} data-visit={item.visit_id}>
                  {checkable.length > 0 && (
                    <td data-label="">
                      {item.can_check && (
                        <input
                          type="checkbox"
                          data-testid="old-entry-pick"
                          aria-label={`Select ${item.number}, visit of ${when.text}`}
                          checked={picked.has(item.visit_id)}
                          onChange={() => setPicked((before) => toggled(before, item.visit_id))}
                        />
                      )}
                    </td>
                  )}
                  <th scope="row" data-label="" className="wrap">
                    <a href={`${base}/accounts/${item.account_id}`} onClick={go} className="name" title={`${item.number} ${item.name}`}>
                      {item.number} {item.name}
                    </a>
                  </th>
                  <td data-label="Visit">{when.text}</td>
                  <td data-label="Entry">
                    <span className="clip" title={oldEntryWhat(item)}>
                      {oldEntryWhat(item)}
                    </span>
                  </td>
                  <td data-label="Driver">
                    {item.driver_name ? (
                      <span className="clip short" title={item.driver_name}>
                        {personName(item.driver_name)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td data-label="Sent" data-testid="old-entry-sent">
                    {oldEntryWhen(item.made_at, timeZone).text}
                    {!item.confirmed && (
                      <span className="sub" data-testid="old-entry-no-confirm">
                        {" "}
                        <Hint text="The driver did not confirm the date on the phone.">
                          <span aria-hidden="true">⚠</span>
                          <span className="visually-hidden">Date not confirmed</span>
                        </Hint>
                      </span>
                    )}
                  </td>
                  <td data-label="" className="row-actions sticky-end">
                    {item.can_check && (
                      <>
                        <a href={`${base}/visits?day=${when.day}`} onClick={go} data-testid="old-entry-correct" aria-label={`Correct the visit of ${item.number}, ${when.text}`}>
                          Correct
                        </a>{" "}
                        <button type="button" data-testid="old-entry-check" disabled={busy} onClick={() => check([item.visit_id])}>
                          Checked
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type ReviewLoad = api.Review | "server" | "network" | "session" | undefined;
type Panel = { key: string; state: ReviewResult | "open" };

/** Design 8.6 and 17.16: the open items of the cycle, and one decision for each. */
export function ReviewPage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const view = useView(canView === true ? REVIEW.name : null, async () => api.review(await user.getIdToken()));
  const found: ReviewLoad = view.data ?? (view.error ? loadFailure(view.error) : undefined);
  const setFound = view.keep;
  const [note, setNote] = useState("");
  const [panel, setPanel] = useState<Panel | null>(null);
  const [sort, setSort] = useSort("sort", REVIEW_COLUMNS);
  const status = useRef<HTMLParagraphElement | null>(null);
  useTitle(`Pickups · ${org.name}`);
  useEffect(() => {
    if (note) status.current?.focus();
  }, [note]);
  if (canView === false) {
    return (
      <main data-testid="review-hidden">
        <h1 tabIndex={-1}>Pickups</h1>
        <p>Your role does not show the review.</p>
      </main>
    );
  }
  const all = typeof found === "object" ? found.kinds.flatMap((group) => group.items) : [];
  const duplicates = typeof found === "object" ? (found.duplicates ?? []) : [];
  const oldEntries = typeof found === "object" ? (found.old_entries ?? []) : [];
  const open = all.length + duplicates.length + oldEntries.length;
  const row = (item: api.ReviewItem, sizes: number[]) => {
    const key = `${item.kind}/${item.account_id}`;
    return (
      <ReviewRow
        key={key}
        item={item}
        base={base}
        sizes={sizes}
        auth={auth}
        user={user}
        panel={panel?.key === key ? panel.state : null}
        onPanel={(state) => setPanel(state ? { key, state } : null)}
        onSaved={(next, text) => {
          setPanel(null);
          setFound(next);
          setNote(text);
        }}
      />
    );
  };
  return (
    <main className="issues-page wide" data-testid="review-page">
      <h1 tabIndex={-1}>Pickups</h1>
      {typeof found === "object" && (
        <p className="sub" data-testid="review-cycle">
          Cycle {dayText(found.cycle_start)} to {dayText(found.cycle_end, true)} · {open === 1 ? "1 open item" : `${open} open items`}
        </p>
      )}
      {typeof found === "object" && (found.place_entries ?? []).length > 0 && (
        <p className="sub" data-testid="review-away-count">
          <a
            href="#away"
            onClick={(event) => {
              event.preventDefault();
              document.getElementById("review-place-entries")?.focus();
            }}
          >
            {awayText((found.place_entries ?? []).length)}
          </a>
        </p>
      )}
      <ViewFreshness view={view} timeZone={org.time_zone} auth={auth} />
      <p role="status" data-testid="review-status" tabIndex={-1} ref={status}>
        {note}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} rows={8} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={view.reload}>
            Try again
          </button>
        </p>
      ) : open === 0 ? (
        <p data-testid="review-none">No open items in this cycle.</p>
      ) : (
        <>
          {duplicates.length > 0 && (
            <Duplicates
              items={duplicates}
              base={base}
              auth={auth}
              user={user}
              onSaved={(next, text) => {
                setFound(next);
                setNote(text);
              }}
            />
          )}
          {oldEntries.length > 0 && (
            <OldEntries
              items={oldEntries}
              base={base}
              timeZone={org.time_zone}
              auth={auth}
              user={user}
              onSaved={(next, text) => {
                setFound(next);
                setNote(text);
              }}
            />
          )}
          {all.length > 0 && (
            <>
              <SortSelect id="review-sort" columns={REVIEW_COLUMNS} sort={sort} onSort={setSort} fallback="By kind, largest gap first" />
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Review items">
                <table className="list review-table" data-testid="review-table" data-cols="review-sort">
                  <thead>
                    <tr>
                      {REVIEW_COLUMNS.map((column) => (
                        <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                      ))}
                      <th scope="col" className="sticky-end">
                        <span className="visually-hidden">Decide</span>
                      </th>
                    </tr>
                  </thead>
                  {sort
                    ? sortRows(all, REVIEW_COLUMNS, sort).map((item) => row(item, found.container_sizes))
                    : found.kinds
                        .filter((group) => group.items.length > 0)
                        .flatMap((group) => [
                          <tbody key={`group-${group.kind}`} className="group" data-testid="review-group" data-kind={group.kind}>
                            <tr>
                              <th scope="rowgroup" colSpan={SPAN} id={`review-${group.kind}`}>
                                {KIND_TEXT[group.kind]} <span className="sub">({countText(group.items.length)})</span>
                              </th>
                            </tr>
                          </tbody>,
                          ...group.items.map((item) => row(item, found.container_sizes)),
                        ])}
                </table>
              </div>
            </>
          )}
        </>
      )}
      {typeof found === "object" && (found.place_entries ?? []).length > 0 && <PlaceEntries items={found.place_entries ?? []} base={base} timeZone={org.time_zone} />}
    </main>
  );
}
